
import React, { useState, useRef, useEffect } from 'react';
import { CycleData } from '@/utils/cycle-tracking';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { MessageSquare, SendIcon, RefreshCw, Bot } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { supabase } from "@/integrations/supabase/client";
import { toast } from 'sonner';

interface AIConsultantTabProps {
  cycleData: CycleData | null;
  isLoading: boolean;
}

interface Message {
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}

const AIConsultantTab: React.FC<AIConsultantTabProps> = ({ cycleData, isLoading }) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Initial welcome message
  useEffect(() => {
    if (messages.length === 0 && cycleData) {
      setMessages([
        {
          role: 'assistant',
          content: `Hi there! I'm your hormone consultant AI. I can answer questions about your menstrual cycle, hormones, and health. Currently, you're in your ${cycleData.currentPhase} phase (day ${cycleData.currentDay} of your cycle). How can I help you today?`,
          timestamp: new Date()
        }
      ]);
    }
  }, [cycleData]);

  // Scroll to bottom of messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!input.trim() || isProcessing) return;
    
    const userMessage = {
      role: 'user' as const,
      content: input.trim(),
      timestamp: new Date()
    };
    
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsProcessing(true);
    
    try {
      const { data, error } = await supabase.functions.invoke('cycle-advice', {
        body: {
          phase: cycleData?.currentPhase || 'unknown',
          day: cycleData?.currentDay || 0,
          cycleLength: cycleData?.cycleLength || 28,
          interests: ['nutrition', 'exercise', 'mood'],
          userQuery: userMessage.content
        }
      });
      
      if (error) throw error;
      
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: data.advice,
        timestamp: new Date()
      }]);
    } catch (error) {
      console.error('Error getting AI response:', error);
      toast.error('Failed to get a response. Please try again.');
      
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: 'Sorry, I encountered an error processing your request. Please try again.',
        timestamp: new Date()
      }]);
    } finally {
      setIsProcessing(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin h-8 w-8 border-4 border-hormony-coral border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card className="border-blue-100">
        <CardHeader className="pb-3">
          <div className="flex justify-between items-center">
            <div>
              <CardTitle className="flex items-center gap-2">
                <MessageSquare className="h-5 w-5 text-[#5a8bff]" />
                Hormone AI Consultant
              </CardTitle>
              <CardDescription>
                Chat with our AI consultant about your hormonal health and cycle questions
              </CardDescription>
            </div>
            {cycleData && (
              <Badge variant="outline" className="capitalize">
                {cycleData.currentPhase} phase • Day {cycleData.currentDay}
              </Badge>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col h-[400px] md:h-[500px]">
            <div className="flex-1 overflow-y-auto mb-4 space-y-4 p-2">
              {messages.map((message, index) => (
                <div 
                  key={index} 
                  className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div 
                    className={`max-w-[80%] rounded-lg p-3 ${
                      message.role === 'user' 
                        ? 'bg-blue-100 text-blue-900' 
                        : 'bg-gray-100 text-gray-800'
                    }`}
                  >
                    {message.role === 'assistant' && (
                      <div className="flex items-center gap-2 mb-1 text-xs text-gray-500">
                        <Bot size={14} />
                        <span>Hormone AI</span>
                      </div>
                    )}
                    <div className="whitespace-pre-wrap">{message.content}</div>
                    <div className="text-xs text-gray-500 mt-1 text-right">
                      {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </div>
                  </div>
                </div>
              ))}
              <div ref={messagesEndRef} />
            </div>
            
            <form onSubmit={handleSubmit} className="flex gap-2">
              <Textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ask about your cycle, hormones, symptoms, or nutrition..."
                className="resize-none min-h-[80px]"
                disabled={isProcessing}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSubmit(e);
                  }
                }}
              />
              <Button 
                type="submit" 
                className="self-end px-4" 
                disabled={isProcessing || !input.trim()}
              >
                {isProcessing ? (
                  <RefreshCw className="h-5 w-5 animate-spin" />
                ) : (
                  <SendIcon className="h-5 w-5" />
                )}
              </Button>
            </form>
          </div>
        </CardContent>
      </Card>
      
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">Sample Questions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <Button 
              variant="outline" 
              className="w-full justify-start text-left h-auto py-2"
              onClick={() => setInput("What foods should I eat during my current phase?")}
            >
              What foods should I eat during my current phase?
            </Button>
            <Button 
              variant="outline" 
              className="w-full justify-start text-left h-auto py-2"
              onClick={() => setInput("Why do I experience mood changes throughout my cycle?")}
            >
              Why do I experience mood changes throughout my cycle?
            </Button>
            <Button 
              variant="outline" 
              className="w-full justify-start text-left h-auto py-2"
              onClick={() => setInput("What exercise is best during my luteal phase?")}
            >
              What exercise is best during my luteal phase?
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AIConsultantTab;
